/**
 * Dark/Light mode theme management with sunrise/sunset automatic switching
 */

import { API_ENDPOINTS } from '../config.js';

const THEME_DARK = 'dark';
const THEME_LIGHT = 'light';
const MANUAL_OVERRIDE_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
const AUTO_CHECK_INTERVAL = 60 * 1000; // Check every minute for precise switching

let sunriseSunsetData = null;
let autoThemeCheckInterval = null;
let manualOverrideUntil = null; // Timestamp when manual override expires

/**
 * Fetch sunrise/sunset data from forecast API
 */
async function fetchSunriseSunsetData() {
  try {
    const response = await fetch(API_ENDPOINTS.FORECAST_DATA);

    if (!response.ok) {
      console.warn('[Theme] Failed to fetch sunrise/sunset data:', response.status);
      return false;
    }

    const data = await response.json();

    if (data?.daily?.[0]) {
      sunriseSunsetData = {
        sunrise: data.daily[0].sunrise,
        sunset: data.daily[0].sunset,
      };

      return true;
    }

    console.warn('[Theme] Invalid forecast data structure');
    return false;
  } catch (error) {
    console.error('[Theme] Error fetching sunrise/sunset data:', error);
    return false;
  }
}

/**
 * Determine if it's currently daytime based on sunrise/sunset
 */
function isDaytime() {
  if (!sunriseSunsetData) {
    // Fallback: assume daytime between 6 AM and 6 PM
    const hour = new Date().getHours();
    return hour >= 6 && hour < 18;
  }

  // Extract just the time-of-day (hours and minutes) for comparison
  // This ignores the date portion, so it works across day boundaries
  const now = new Date();
  const sunrise = new Date(sunriseSunsetData.sunrise * 1000);
  const sunset = new Date(sunriseSunsetData.sunset * 1000);

  // Convert to minutes since midnight for easy comparison
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const sunriseMinutes = sunrise.getHours() * 60 + sunrise.getMinutes();
  const sunsetMinutes = sunset.getHours() * 60 + sunset.getMinutes();

  // It's daytime if current time is between sunrise and sunset (time only)
  // Midnight to sunrise: dark
  // Sunrise to sunset: light
  // Sunset to midnight: dark
  return nowMinutes >= sunriseMinutes && nowMinutes < sunsetMinutes;
}

/**
 * Get the theme that should be active based on time of day
 */
function getAutomaticTheme() {
  return isDaytime() ? THEME_LIGHT : THEME_DARK;
}

/**
 * Check if manual override is still active
 */
function isManualOverrideActive() {
  if (!manualOverrideUntil) {
    return false;
  }

  if (Date.now() >= manualOverrideUntil) {
    manualOverrideUntil = null;
    return false;
  }

  return true;
}

/**
 * Get the currently active theme
 */
function getCurrentTheme() {
  // Check what's currently applied to the DOM
  return document.documentElement.hasAttribute('data-theme') ? THEME_DARK : THEME_LIGHT;
}

/**
 * Apply theme to document
 * @param {string} theme - 'light' or 'dark'
 * @param {boolean} isManual - If true, this is a manual override
 */
function applyTheme(theme, isManual = false) {

  // Apply theme to DOM
  if (theme === THEME_DARK) {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }

  // Update button icon
  updateThemeIcon(theme);

  // Update Highcharts theme
  updateChartsTheme(theme);

  // Handle manual override
  if (isManual) {
    manualOverrideUntil = Date.now() + MANUAL_OVERRIDE_TIMEOUT;
  }
}

/**
 * Toggle between light and dark theme (manual override)
 */
export function toggleTheme() {
  const currentTheme = getCurrentTheme();
  const newTheme = currentTheme === THEME_DARK ? THEME_LIGHT : THEME_DARK;
  applyTheme(newTheme, true);
}

/**
 * Update theme toggle button icon and label
 * @param {string} theme - Current theme
 */
function updateThemeIcon(theme) {
  const icon = document.getElementById('theme-icon');
  const label = document.getElementById('theme-label');

  if (icon) {
    // Show sun icon in dark mode (to switch to light), moon in light mode (to switch to dark)
    icon.textContent = theme === THEME_DARK ? '☀️' : '🌙';
  }

  if (label) {
    // Show what mode will be activated on click
    label.textContent = theme === THEME_DARK ? 'Light Mode' : 'Dark Mode';
  }
}

/**
 * Check and apply automatic theme if needed
 */
function checkAndApplyAutoTheme() {
  // Skip if manual override is active
  if (isManualOverrideActive()) {
    return;
  }

  // Get what theme should be active
  const targetTheme = getAutomaticTheme();
  const currentTheme = getCurrentTheme();

  // Apply if different
  if (targetTheme !== currentTheme) {
    applyTheme(targetTheme, false);
  }
}

/**
 * Update Highcharts theme based on current mode
 * @param {string} theme - Current theme
 */
function updateChartsTheme(theme) {
  if (typeof Highcharts === 'undefined') {
    return;
  }

  const isDark = theme === THEME_DARK;

  const chartTheme = {
    colors: isDark
      ? ['#2b908f', '#90ee7e', '#f45b5b', '#7798BF', '#aaeeee', '#ff0066', '#eeaaee', '#55BF3B', '#DF5353', '#7798BF', '#aaeeee']
      : ['#7cb5ec', '#434348', '#90ed7d', '#f7a35c', '#8085e9', '#f15c80', '#e4d354', '#2b908f', '#f45b5b', '#91e8e1'],
    chart: {
      backgroundColor: isDark ? '#1a1a1a' : '#fefefe',
      style: {
        fontFamily: '\'GitLabSans\', sans-serif',
        color: isDark ? '#E0E0E3' : '#333333',
      },
      plotBorderColor: isDark ? '#606063' : '#cccccc',
    },
    title: {
      style: {
        color: isDark ? '#E0E0E3' : '#333333',
        textTransform: 'uppercase',
        fontSize: '20px',
      },
    },
    subtitle: {
      style: {
        color: isDark ? '#E0E0E3' : '#666666',
        textTransform: 'uppercase',
      },
    },
    xAxis: {
      gridLineColor: isDark ? '#4a4a4a' : '#e6e6e6',
      labels: {
        style: {
          color: isDark ? '#E0E0E3' : '#666666',
        },
      },
      lineColor: isDark ? '#707073' : '#ccd6eb',
      minorGridLineColor: isDark ? '#505053' : '#e6e6e6',
      tickColor: isDark ? '#707073' : '#ccd6eb',
      title: {
        style: {
          color: isDark ? '#A0A0A3' : '#666666',
        },
      },
    },
    yAxis: {
      gridLineColor: isDark ? '#4a4a4a' : '#e6e6e6',
      labels: {
        style: {
          color: isDark ? '#E0E0E3' : '#666666',
        },
      },
      lineColor: isDark ? '#707073' : '#ccd6eb',
      minorGridLineColor: isDark ? '#505053' : '#e6e6e6',
      tickColor: isDark ? '#707073' : '#ccd6eb',
      tickWidth: 1,
      title: {
        style: {
          color: isDark ? '#A0A0A3' : '#666666',
        },
      },
    },
    tooltip: {
      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.85)' : 'rgba(247,247,247,0.85)',
      style: {
        color: isDark ? '#F0F0F0' : '#333333',
      },
    },
    pane: {
      background: {
        backgroundColor: isDark ? '#2a2a2a' : '#f0f0f0',
        borderColor: isDark ? '#4a4a4a' : '#cccccc',
      },
    },
    plotOptions: {
      series: {
        dataLabels: {
          color: isDark ? '#F0F0F3' : '#000000',
          style: {
            fontSize: '13px',
            textOutline: 'none',
          },
        },
        marker: {
          lineColor: isDark ? '#333' : '#ffffff',
        },
      },
      solidgauge: {
        dataLabels: {
          color: isDark ? '#F0F0F3' : '#000000',
          style: {
            fontSize: '24px',
            textOutline: 'none',
          },
        },
      },
      boxplot: {
        fillColor: isDark ? '#505053' : '#ffffff',
      },
      candlestick: {
        lineColor: isDark ? '#ffffff' : '#000000',
      },
      errorbar: {
        color: isDark ? '#ffffff' : '#000000',
      },
    },
    legend: {
      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)',
      itemStyle: {
        color: isDark ? '#E0E0E3' : '#333333',
      },
      itemHoverStyle: {
        color: isDark ? '#FFF' : '#000',
      },
      itemHiddenStyle: {
        color: isDark ? '#606063' : '#cccccc',
      },
      title: {
        style: {
          color: isDark ? '#C0C0C0' : '#333333',
        },
      },
    },
    credits: {
      style: {
        color: isDark ? '#666' : '#999999',
      },
    },
    labels: {
      style: {
        color: isDark ? '#707073' : '#666666',
      },
    },
    drilldown: {
      activeAxisLabelStyle: {
        color: isDark ? '#F0F0F3' : '#003399',
      },
      activeDataLabelStyle: {
        color: isDark ? '#F0F0F3' : '#003399',
      },
    },
    navigation: {
      buttonOptions: {
        symbolStroke: isDark ? '#DDDDDD' : '#666666',
        theme: {
          fill: isDark ? '#505053' : '#ffffff',
        },
      },
    },
    // Scroll charts
    rangeSelector: {
      buttonTheme: {
        fill: isDark ? '#505053' : '#ffffff',
        stroke: isDark ? '#000000' : '#cccccc',
        style: {
          color: isDark ? '#CCC' : '#333333',
        },
        states: {
          hover: {
            fill: isDark ? '#707073' : '#e6e6e6',
            stroke: isDark ? '#000000' : '#cccccc',
            style: {
              color: 'white',
            },
          },
          select: {
            fill: isDark ? '#000003' : '#e6e6e6',
            stroke: isDark ? '#000000' : '#cccccc',
            style: {
              color: 'white',
            },
          },
        },
      },
      inputBoxBorderColor: isDark ? '#505053' : '#cccccc',
      inputStyle: {
        backgroundColor: isDark ? '#333' : '#ffffff',
        color: isDark ? 'silver' : '#333333',
      },
      labelStyle: {
        color: isDark ? 'silver' : '#666666',
      },
    },
    navigator: {
      handles: {
        backgroundColor: isDark ? '#666' : '#cccccc',
        borderColor: isDark ? '#AAA' : '#999999',
      },
      outlineColor: isDark ? '#CCC' : '#cccccc',
      maskFill: isDark ? 'rgba(255,255,255,.1)' : 'rgba(102,133,194,0.3)',
      series: {
        color: isDark ? '#7798BF' : '#7cb5ec',
        lineColor: isDark ? '#A6C7ED' : '#4572A7',
      },
      xAxis: {
        gridLineColor: isDark ? '#505053' : '#e6e6e6',
      },
    },
    scrollbar: {
      barBackgroundColor: isDark ? '#808083' : '#cccccc',
      barBorderColor: isDark ? '#808083' : '#cccccc',
      buttonArrowColor: isDark ? '#CCC' : '#666666',
      buttonBackgroundColor: isDark ? '#606063' : '#e6e6e6',
      buttonBorderColor: isDark ? '#606063' : '#cccccc',
      rifleColor: isDark ? '#FFF' : '#666666',
      trackBackgroundColor: isDark ? '#404043' : '#f2f2f2',
      trackBorderColor: isDark ? '#404043' : '#e6e6e6',
    },
  };

  // Apply theme to Highcharts GLOBALLY
  Highcharts.setOptions(chartTheme);

  // Redraw all existing charts with full theme
  Highcharts.charts.forEach(chart => {
    if (!chart || !chart.renderTo || chart.chartHeight === undefined) {
      return;
    }

    try {
      // For gauge/solidgauge charts, use simpler update without pane
      const isGauge = chart.options.chart && (
        chart.options.chart.type === 'solidgauge' ||
        chart.options.chart.type === 'gauge'
      );

      const updateOptions = {
        chart: chartTheme.chart,
        title: chartTheme.title,
        subtitle: chartTheme.subtitle,
        tooltip: chartTheme.tooltip,
        plotOptions: chartTheme.plotOptions,
        legend: chartTheme.legend,
      };

      // Add pane updates for gauge charts, axis updates for non-gauge charts
      if (isGauge) {
        // Check if this is the dual-ring temperature gauge (chart-dp)
        if (chart.renderTo.id === 'chart-dp') {
          // Special handling for dual-ring gauge
          updateOptions.pane = {
            background: [{
              outerRadius: '100%',
              innerRadius: '76%',
              shape: 'arc',
              backgroundColor: chartTheme.pane.background.backgroundColor,
              borderColor: chartTheme.pane.background.borderColor,
            }, {
              outerRadius: '75%',
              innerRadius: '49%',
              shape: 'arc',
              backgroundColor: chartTheme.pane.background.backgroundColor,
              borderColor: chartTheme.pane.background.borderColor,
            }],
          };
        } else {
          updateOptions.pane = chartTheme.pane;
        }
      } else {
        updateOptions.xAxis = chartTheme.xAxis;
        updateOptions.yAxis = chartTheme.yAxis;
        updateOptions.pane = chartTheme.pane;
      }

      chart.update(updateOptions, false); // Don't redraw yet
    } catch (error) {
      console.warn('Failed to update chart theme:', error);
    }
  });

  // Single redraw after all updates
  Highcharts.charts.forEach(chart => {
    if (chart && chart.renderTo && chart.chartHeight !== undefined) {
      try {
        chart.redraw();
      } catch {
        // Silently ignore redraw errors
      }
    }
  });
}

/**
 * Initialize theme system
 */
export async function initializeTheme() {
  // Fetch sunrise/sunset data
  const dataLoaded = await fetchSunriseSunsetData();

  if (!dataLoaded) {
    console.warn('[Theme] Failed to load sunrise/sunset data, using fallback');
  }

  // Apply initial theme based on current time
  const initialTheme = getAutomaticTheme();
  applyTheme(initialTheme, false);

  // Set up theme toggle button using event delegation (button is dynamically created)
  document.addEventListener('click', (event) => {
    if (event.target.closest('#theme-toggle')) {
      toggleTheme();
    }
  });

  // Set up automatic checking every minute
  if (autoThemeCheckInterval) {
    clearInterval(autoThemeCheckInterval);
  }
  autoThemeCheckInterval = setInterval(checkAndApplyAutoTheme, AUTO_CHECK_INTERVAL);

  // Also refresh sunrise/sunset data every hour
  setInterval(async () => {
    await fetchSunriseSunsetData();
    checkAndApplyAutoTheme();
  }, 60 * 60 * 1000);
}

// Apply a quick initial theme based on time to prevent flash
// This will be corrected once sunrise/sunset data loads
if (typeof window !== 'undefined') {
  const hour = new Date().getHours();
  const likelyDark = hour < 6 || hour >= 18;
  if (likelyDark) {
    document.documentElement.setAttribute('data-theme', 'dark');
  }
}